import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Megaphone, ClipboardList, Clock, Repeat, Globe, FileText, Eye, LayoutDashboard, Images, X, CalendarClock, CalendarCheck, CalendarX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../utils/api';
import MediaPickerModal from '../components/MediaPickerModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Returns "YYYY-MM-DD" in local time for date input defaultValue
const toDateInputValue = (iso) => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

// Returns readable "Mon DD, YYYY" from "YYYY-MM-DD"
const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return new Date(+y, +m - 1, +day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Derive display status factoring in schedule window
const getScheduleState = (p) => {
  const today = new Date().toISOString().slice(0, 10);
  const start = p.schedule_start || null;
  const end = p.schedule_end || null;
  if (p.status !== 'active') return { state: 'draft', label: 'Draft', color: 'secondary' };
  if (start && today < start) return { state: 'pending', label: 'Scheduled', color: 'outline' };
  if (end && today > end) return { state: 'expired', label: 'Expired', color: 'destructive' };
  return { state: 'live', label: 'Live', color: 'default' };
};

const EMPTY_POPUP = {
  name: '',
  type: 'announcement',
  status: 'draft',
  image_url: '',
  title: '',
  body: '',
  cta_text: '',
  cta_url: '',
  form_id: '',
  form_title: '',
  trigger: 'immediate',
  delay_seconds: 3,
  frequency: 'session',
  show_on_all_pages: false,
  page_ids: [],
  schedule_start: '',
  schedule_end: '',
};

export default function Popups() {
  const [popups, setPopups] = useState([]);
  const [pages, setPages] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null); // popup object or null (create)
  const [form, setForm] = useState(EMPTY_POPUP);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [previewPopup, setPreviewPopup] = useState(null); // popup to preview

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [popupRes, pageRes, formRes] = await Promise.all([
        api.get('/popups'),
        api.get('/pages'),
        api.get('/forms'),
      ]);
      setPopups(popupRes.data || []);
      setPages(pageRes.data || []);
      setForms(formRes.data || []);
    } catch {
      toast.error('Failed to load popups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_POPUP);
    setShowDialog(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY_POPUP, ...p });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Give this popup a name'); return; }
    if (form.schedule_start && form.schedule_end && form.schedule_start > form.schedule_end) {
      toast.error('End date must be after start date');
      return;
    }
    setSaving(true);
    // Pass schedule dates as-is: '' lets backend clear them, null is ignored by PUT handler
    const payload = { ...form };
    try {
      if (editing) {
        await api.put(`/popups/${editing.popup_id}`, payload);
        toast.success('Popup updated');
      } else {
        await api.post('/popups', payload);
        toast.success('Popup created');
      }
      setShowDialog(false);
      fetchAll();
    } catch {
      toast.error('Failed to save popup');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (p) => {
    const next = p.status === 'active' ? 'draft' : 'active';
    try {
      await api.put(`/popups/${p.popup_id}`, { status: next });
      setPopups(prev => prev.map(x => x.popup_id === p.popup_id ? { ...x, status: next } : x));
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/popups/${deleteTarget.popup_id}`);
      toast.success('Popup deleted');
      setDeleteTarget(null);
      fetchAll();
    } catch { toast.error('Failed to delete popup'); }
  };

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const togglePageId = (pageId) => {
    setForm(f => ({
      ...f,
      page_ids: f.page_ids.includes(pageId)
        ? f.page_ids.filter(id => id !== pageId)
        : [...f.page_ids, pageId],
    }));
  };

  const frequencyLabel = { session: 'Once per session', once: 'Once ever', always: 'Every visit' };
  const triggerLabel = { immediate: 'Immediately', delayed: `After ${form.delay_seconds}s` };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors mb-2">
            <LayoutDashboard size={14} /> Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Popups</h1>
          <p className="text-sm text-gray-500 mt-1">Show announcements or contact forms when visitors open a page</p>
        </div>
        <Button onClick={openCreate} data-testid="create-popup-btn" className="flex items-center gap-2">
          <Plus size={16} /> Create Popup
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">Loading…</div>
      ) : popups.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-gray-200 rounded-2xl">
          <Megaphone size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No popups yet</p>
          <p className="text-sm text-gray-400 mb-4">Create your first popup to engage visitors</p>
          <Button onClick={openCreate} variant="outline">Create your first popup</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {popups.map(p => (
            <div key={p.popup_id} data-testid={`popup-row-${p.popup_id}`}
              className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-5 py-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${p.type === 'form' ? 'bg-purple-50' : 'bg-blue-50'}`}>
                  {p.type === 'form' ? <ClipboardList size={18} className="text-purple-600" /> : <Megaphone size={18} className="text-blue-600" />}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{p.name}</span>
                    {(() => {
                      const { state, label, color } = getScheduleState(p);
                      const variantMap = { default: 'default', secondary: 'secondary', outline: 'outline', destructive: 'destructive' };
                      return (
                        <Badge variant={variantMap[color]} className={`text-xs capitalize ${state === 'pending' ? 'border-amber-400 text-amber-700' : state === 'expired' ? 'bg-red-100 text-red-700 border-red-200' : ''}`}
                          data-testid={`popup-status-badge-${p.popup_id}`}>
                          {state === 'pending' && <CalendarClock size={10} className="mr-1" />}
                          {state === 'expired' && <CalendarX size={10} className="mr-1" />}
                          {state === 'live' && <CalendarCheck size={10} className="mr-1" />}
                          {label}
                        </Badge>
                      );
                    })()}
                    <Badge variant="outline" className="text-xs capitalize">{p.type}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                    <span className="flex items-center gap-1"><Clock size={11} /> {p.trigger === 'delayed' ? `After ${p.delay_seconds}s` : 'Immediate'}</span>
                    <span className="flex items-center gap-1"><Repeat size={11} /> {frequencyLabel[p.frequency] || p.frequency}</span>
                    <span className="flex items-center gap-1">
                      <Globe size={11} />
                      {p.show_on_all_pages ? 'All pages' : `${p.page_ids?.length || 0} page${p.page_ids?.length !== 1 ? 's' : ''}`}
                    </span>
                    {(p.schedule_start || p.schedule_end) && (
                      <span className="flex items-center gap-1 text-amber-600" data-testid={`popup-schedule-${p.popup_id}`}>
                        <CalendarClock size={11} />
                        {p.schedule_start && p.schedule_end
                          ? `${fmtDate(p.schedule_start)} – ${fmtDate(p.schedule_end)}`
                          : p.schedule_start
                          ? `From ${fmtDate(p.schedule_start)}`
                          : `Until ${fmtDate(p.schedule_end)}`}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreviewPopup(p)}
                  data-testid={`popup-preview-${p.popup_id}`}
                  title="Preview"
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors"
                >
                  <Eye size={16} />
                </button>
                <button
                  onClick={() => toggleStatus(p)}
                  data-testid={`popup-toggle-${p.popup_id}`}
                  title={p.status === 'active' ? 'Deactivate' : 'Activate'}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {p.status === 'active'
                    ? <ToggleRight size={22} className="text-green-500" />
                    : <ToggleLeft size={22} className="text-gray-400" />}
                </button>
                <button onClick={() => openEdit(p)} data-testid={`popup-edit-${p.popup_id}`}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors">
                  <Pencil size={16} />
                </button>
                <button onClick={() => setDeleteTarget(p)} data-testid={`popup-delete-${p.popup_id}`}
                  className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="popup-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Popup' : 'Create Popup'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Name */}
            <div>
              <Label className="text-sm font-medium">Internal Name *</Label>
              <Input placeholder="e.g. Open Day Announcement" value={form.name}
                onChange={e => setField('name', e.target.value)} data-testid="popup-name-input" className="mt-1" />
            </div>

            {/* Type */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Popup Type</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { val: 'announcement', icon: Megaphone, label: 'Announcement', desc: 'Image + text + button' },
                  { val: 'form', icon: ClipboardList, label: 'Contact Form', desc: 'Show one of your forms' },
                ].map(opt => (
                  <button key={opt.val} type="button" data-testid={`popup-type-${opt.val}`}
                    onClick={() => setField('type', opt.val)}
                    className={`flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${form.type === opt.val ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <opt.icon size={18} className={form.type === opt.val ? 'text-blue-600 mt-0.5' : 'text-gray-400 mt-0.5'} />
                    <div>
                      <div className="font-medium text-sm">{opt.label}</div>
                      <div className="text-xs text-gray-500">{opt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Announcement fields */}
            {form.type === 'announcement' && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
                <div>
                  <Label className="text-sm font-medium">Image <span className="text-gray-400 font-normal">(optional)</span></Label>
                  <div className="flex gap-2 mt-1">
                    <Input placeholder="https://... or pick from gallery" value={form.image_url}
                      onChange={e => setField('image_url', e.target.value)} data-testid="popup-image-url" className="flex-1" />
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowMediaPicker(true)}
                      data-testid="popup-image-gallery-btn" className="flex items-center gap-1.5 shrink-0">
                      <Images size={14} /> Gallery
                    </Button>
                  </div>
                  {form.image_url && (
                    <div className="mt-2 relative w-full aspect-video rounded-lg overflow-hidden border border-gray-200">
                      <img src={form.image_url} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setField('image_url', '')}
                        className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors">
                        <X size={12} />
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-sm font-medium">Title</Label>
                  <Input placeholder="Admissions Open 2026" value={form.title}
                    onChange={e => setField('title', e.target.value)} className="mt-1" data-testid="popup-title-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Body Text</Label>
                  <Textarea placeholder="Add a short description…" value={form.body} rows={3}
                    onChange={e => setField('body', e.target.value)} className="mt-1" data-testid="popup-body-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Button Label</Label>
                    <Input placeholder="Enroll Now" value={form.cta_text}
                      onChange={e => setField('cta_text', e.target.value)} className="mt-1" data-testid="popup-cta-text" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Button URL</Label>
                    <Input placeholder="https://..." value={form.cta_url}
                      onChange={e => setField('cta_url', e.target.value)} className="mt-1" data-testid="popup-cta-url" />
                  </div>
                </div>
              </div>
            )}

            {/* Form fields */}
            {form.type === 'form' && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
                <div>
                  <Label className="text-sm font-medium">Popup Heading</Label>
                  <Input placeholder="Talk to Us" value={form.form_title}
                    onChange={e => setField('form_title', e.target.value)} className="mt-1" data-testid="popup-form-title" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Select Form</Label>
                  {forms.length === 0 ? (
                    <p className="text-sm text-gray-500 mt-1">No forms found. <a href="/forms" className="text-blue-600 underline">Create one first →</a></p>
                  ) : (
                    <Select value={form.form_id || ''} onValueChange={v => setField('form_id', v)}>
                      <SelectTrigger className="mt-1" data-testid="popup-form-select">
                        <SelectValue placeholder="Choose a form…" />
                      </SelectTrigger>
                      <SelectContent>
                        {forms.map(f => (
                          <SelectItem key={f.form_id} value={f.form_id}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            )}

            {/* Display settings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Trigger</Label>
                <Select value={form.trigger} onValueChange={v => setField('trigger', v)}>
                  <SelectTrigger className="mt-1" data-testid="popup-trigger-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">Immediately on load</SelectItem>
                    <SelectItem value="delayed">After a delay</SelectItem>
                  </SelectContent>
                </Select>
                {form.trigger === 'delayed' && (
                  <div className="flex items-center gap-2 mt-2">
                    <Input type="number" min={1} max={30} value={form.delay_seconds}
                      onChange={e => setField('delay_seconds', parseInt(e.target.value) || 3)}
                      className="w-20" data-testid="popup-delay-seconds" />
                    <span className="text-sm text-gray-500">seconds</span>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-sm font-medium">Show frequency</Label>
                <Select value={form.frequency} onValueChange={v => setField('frequency', v)}>
                  <SelectTrigger className="mt-1" data-testid="popup-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="session">Once per session</SelectItem>
                    <SelectItem value="once">Once ever (per device)</SelectItem>
                    <SelectItem value="always">Every visit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Page assignment */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Pages</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Show on all pages</span>
                  <Switch
                    checked={form.show_on_all_pages}
                    onCheckedChange={v => setField('show_on_all_pages', v)}
                    data-testid="popup-all-pages-toggle"
                  />
                </div>
              </div>

              {!form.show_on_all_pages && (
                <div className="border border-gray-200 rounded-xl max-h-48 overflow-y-auto p-2 space-y-1">
                  {pages.length === 0 ? (
                    <p className="text-sm text-gray-400 p-2">No pages found</p>
                  ) : pages.map(pg => (
                    <label key={pg.page_id}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${form.page_ids.includes(pg.page_id) ? 'bg-blue-50' : ''}`}>
                      <input type="checkbox" checked={form.page_ids.includes(pg.page_id)}
                        onChange={() => togglePageId(pg.page_id)}
                        data-testid={`popup-page-${pg.page_id}`}
                        className="rounded text-blue-600" />
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-gray-400" />
                        <span className="text-sm text-gray-700">{pg.title || pg.slug}</span>
                        <span className="text-xs text-gray-400">/{pg.slug}</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              {!form.show_on_all_pages && form.page_ids.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">Select at least one page, or enable "Show on all pages"</p>
              )}
            </div>

            {/* Schedule */}
            <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <CalendarClock size={16} className="text-amber-600" />
                <span className="text-sm font-medium text-amber-900">Schedule <span className="font-normal text-amber-600">(optional)</span></span>
              </div>
              <p className="text-xs text-amber-700 leading-relaxed -mt-1">
                Set dates to auto-show and auto-hide this popup. Leave blank to follow the status toggle only.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-amber-800">Start date</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <Input
                      type="date"
                      value={toDateInputValue(form.schedule_start)}
                      onChange={e => setField('schedule_start', e.target.value)}
                      className="flex-1 text-sm"
                      data-testid="popup-schedule-start"
                    />
                    {form.schedule_start && (
                      <button type="button" onClick={() => setField('schedule_start', '')}
                        className="text-gray-400 hover:text-gray-600" title="Clear">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-amber-800">End date</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <Input
                      type="date"
                      value={toDateInputValue(form.schedule_end)}
                      onChange={e => setField('schedule_end', e.target.value)}
                      className="flex-1 text-sm"
                      data-testid="popup-schedule-end"
                    />
                    {form.schedule_end && (
                      <button type="button" onClick={() => setField('schedule_end', '')}
                        className="text-gray-400 hover:text-gray-600" title="Clear">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {form.schedule_start && form.schedule_end && form.schedule_start > form.schedule_end && (
                <p className="text-xs text-red-600">End date must be after start date.</p>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div>
                <div className="font-medium text-sm">Status</div>
                <div className="text-xs text-gray-500">{form.status === 'active' ? 'Live — visitors will see this popup' : 'Draft — hidden from visitors'}</div>
              </div>
              <Switch
                checked={form.status === 'active'}
                onCheckedChange={v => setField('status', v ? 'active' : 'draft')}
                data-testid="popup-status-toggle"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="popup-save-btn">
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Popup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Popup?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            "<strong>{deleteTarget?.name}</strong>" will be permanently removed.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="popup-delete-confirm-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Picker */}
      <MediaPickerModal
        open={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={(media) => {
          setField('image_url', media.url);
          setShowMediaPicker(false);
        }}
      />

      {/* Live Preview Modal */}
      {previewPopup && (
        <>
          <div className="fixed inset-0 bg-black/60 z-[9998]" onClick={() => setPreviewPopup(null)} />
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
              {/* Preview label */}
              <div className="absolute top-0 left-0 right-0 bg-amber-400 text-amber-900 text-xs font-semibold text-center py-1 z-10">
                PREVIEW — how visitors will see this popup
              </div>
              <button
                onClick={() => setPreviewPopup(null)}
                data-testid="preview-close-btn"
                className="absolute top-7 right-3 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 hover:bg-gray-100 text-gray-500 shadow-sm"
              >
                <X size={16} />
              </button>

              {/* Announcement preview */}
              {previewPopup.type === 'announcement' && (
                <div className="pt-7">
                  {previewPopup.image_url && (
                    <div className="w-full aspect-video overflow-hidden">
                      <img src={previewPopup.image_url} alt={previewPopup.title || ''} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6">
                    {previewPopup.title && <h2 className="text-xl font-bold text-gray-900 mb-2">{previewPopup.title}</h2>}
                    {previewPopup.body && <p className="text-gray-600 text-sm leading-relaxed mb-4">{previewPopup.body}</p>}
                    {previewPopup.cta_text && previewPopup.cta_url && (
                      <span className="inline-block w-full text-center bg-blue-600 text-white font-semibold py-2.5 px-6 rounded-lg text-sm cursor-default">
                        {previewPopup.cta_text}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Form preview */}
              {previewPopup.type === 'form' && (
                <div className="p-6 pt-10">
                  {previewPopup.form_title && <h2 className="text-xl font-bold text-gray-900 mb-4">{previewPopup.form_title}</h2>}
                  <p className="text-sm text-gray-500 italic">Form fields will appear here when the popup is live on the page.</p>
                  <div className="mt-4 bg-gray-50 rounded-lg p-3 text-xs text-gray-400">
                    Connected form: <strong className="text-gray-600">{forms.find(f => f.form_id === previewPopup.form_id)?.name || previewPopup.form_id || '—'}</strong>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
